import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Link2 } from "lucide-react";

export default function Estates() {
  const [estates, setEstates] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", description: "" });
  const [assignManagerId, setAssignManagerId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, m] = await Promise.all([
        api.get("/estates"),
        api.get("/users", { params: { role: "estate_manager" } }),
      ]);
      setEstates(e.data);
      setManagers(m.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/estates/${editing.id}`, form);
        toast.success("Estate updated");
      } else {
        await api.post("/estates", form);
        toast.success("Estate created");
      }
      setOpenCreate(false); setEditing(null);
      setForm({ name: "", address: "", description: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this estate? All units under it will also be removed.")) return;
    try {
      await api.delete(`/estates/${id}`);
      toast.success("Estate deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openAssign = (estate) => {
    setAssigning(estate);
    setAssignManagerId(estate.manager_id || "");
  };

  const submitAssign = async () => {
    try {
      await api.patch(`/estates/${assigning.id}`, { manager_id: assignManagerId || null });
      toast.success("Manager assigned");
      setAssigning(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow="Super Admin"
          title="Estates"
          description="Create, edit and delete estates. Assign an estate manager to each."
          actions={
            <Button
              onClick={() => { setEditing(null); setForm({ name: "", address: "", description: "" }); setOpenCreate(true); }}
              data-testid="create-estate-button"
              className="rounded-none bg-black hover:bg-zinc-800 h-10 px-4 uppercase tracking-wider text-xs font-semibold"
            >
              <Plus className="h-4 w-4" /> New Estate
            </Button>
          }
        />

        {loading ? (
          <div className="label-eyebrow">Loading…</div>
        ) : estates.length === 0 ? (
          <EmptyState
            title="No estates yet"
            description="Create your first estate to start onboarding managers and residents."
            action={
              <Button onClick={() => setOpenCreate(true)} data-testid="empty-create-estate" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">
                <Plus className="h-4 w-4" /> Create Estate
              </Button>
            }
          />
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr className="text-left">
                  <th className="p-4 label-eyebrow font-semibold">Estate</th>
                  <th className="p-4 label-eyebrow font-semibold">Address</th>
                  <th className="p-4 label-eyebrow font-semibold">Manager</th>
                  <th className="p-4 label-eyebrow font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {estates.map((e) => (
                  <tr key={e.id} className="border-b border-zinc-100" data-testid={`estate-row-${e.id}`}>
                    <td className="p-4">
                      <div className="font-semibold">{e.name}</div>
                      {e.description && <div className="text-xs text-zinc-500 mt-0.5 max-w-md truncate">{e.description}</div>}
                    </td>
                    <td className="p-4 text-zinc-600">{e.address || "—"}</td>
                    <td className="p-4">
                      {e.manager ? (
                        <div>
                          <div className="font-medium">{e.manager.name}</div>
                          <div className="text-xs text-zinc-500">{e.manager.email}</div>
                        </div>
                      ) : <span className="text-xs text-zinc-400 uppercase tracking-wider">Unassigned</span>}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openAssign(e)}
                          data-testid={`assign-manager-${e.id}`}
                          className="p-2 border border-zinc-200 hover:bg-black hover:text-white hover:border-black transition-colors"
                          title="Assign manager"
                        ><Link2 className="h-3.5 w-3.5" /></button>
                        <button
                          onClick={() => { setEditing(e); setForm({ name: e.name, address: e.address || "", description: e.description || "" }); setOpenCreate(true); }}
                          data-testid={`edit-estate-${e.id}`}
                          className="p-2 border border-zinc-200 hover:bg-black hover:text-white hover:border-black transition-colors"
                        ><Pencil className="h-3.5 w-3.5" /></button>
                        <button
                          onClick={() => onDelete(e.id)}
                          data-testid={`delete-estate-${e.id}`}
                          className="p-2 border border-zinc-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                        ><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit */}
        <Dialog open={openCreate} onOpenChange={(v) => { setOpenCreate(v); if (!v) setEditing(null); }}>
          <DialogContent className="rounded-none border-zinc-300" data-testid="estate-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                {editing ? "Edit Estate" : "New Estate"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submitCreate} className="space-y-4">
              <div>
                <Label className="label-eyebrow">Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="estate-name-input"
                  className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              <div>
                <Label className="label-eyebrow">Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="estate-address-input"
                  className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              <div>
                <Label className="label-eyebrow">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="estate-description-input"
                  className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-estate-button" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">
                  {editing ? "Save Changes" : "Create Estate"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Assign manager */}
        <Dialog open={!!assigning} onOpenChange={(v) => !v && setAssigning(null)}>
          <DialogContent className="rounded-none border-zinc-300" data-testid="assign-manager-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">Assign Manager</DialogTitle>
            </DialogHeader>
            <div>
              <Label className="label-eyebrow">Estate Manager</Label>
              <Select value={assignManagerId || "none"} onValueChange={(v) => setAssignManagerId(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="assign-manager-select">
                  <SelectValue placeholder="Select a manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassign —</SelectItem>
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name} ({m.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {managers.length === 0 && (
                <div className="text-xs text-zinc-500 mt-2">Create a manager first in the Managers page.</div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={submitAssign} data-testid="submit-assign-manager" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </AppShell>
  );
}
